import { useEffect, useRef, useState } from 'react';
import './MobileNavMenu.css';

// Custom smooth horizontal scroll with fast animation (75ms for snappy response)
const smoothScrollToCenter = (container, element, duration = 75) => {
  if (!container || !element) return;
  
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  
  // Calculate target scroll position to center the element
  const elementCenter = elementRect.left + elementRect.width / 2;
  const containerCenter = containerRect.left + containerRect.width / 2;
  const scrollOffset = elementCenter - containerCenter;
  const targetScrollLeft = container.scrollLeft + scrollOffset;
  
  const startScrollLeft = container.scrollLeft;
  const difference = targetScrollLeft - startScrollLeft;
  const startTime = performance.now();
  
  // Ease-out cubic for snappy feel
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  
  const animateScroll = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);
    
    container.scrollLeft = startScrollLeft + difference * easedProgress;
    
    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  };
  
  requestAnimationFrame(animateScroll);
};

export const MobileNavMenu = ({ items, className = '' }) => {
  const listRef = useRef(null);
  const activeItemRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(60);
  const [isAtBottom, setIsAtBottom] = useState(false);
  // Track if scroll was triggered by click (to skip useEffect scroll)
  const isClickScrollRef = useRef(false);

  // Calculate header height and position menu below it
  // Syncs with FX.StickyHeader behavior (.page-header collapses on scroll)
  useEffect(() => {
    const updateHeaderHeight = () => {
      const pageHeader = document.querySelector('.page-header');
      if (pageHeader) {
        setHeaderHeight(pageHeader.offsetHeight);
      }
    };

    // Initial calculation
    updateHeaderHeight();

    // Recalculate on resize and scroll (header changes size on scroll)
    window.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('scroll', updateHeaderHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('scroll', updateHeaderHeight);
    };
  }, []);

  // Track container boundaries and switch positioning
  useEffect(() => {
    const checkContainerBounds = () => {
      const container = document.querySelector('.seo-checker-app');
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const menuHeight = 60; // Approximate menu height
      
      // Check if the bottom of the container is above where the fixed menu would be
      // (viewport top + headerHeight + menuHeight)
      const fixedMenuBottom = headerHeight + menuHeight;
      
      if (containerRect.bottom <= fixedMenuBottom) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    };

    // Initial check
    checkContainerBounds();

    // Check on scroll and resize
    window.addEventListener('scroll', checkContainerBounds);
    window.addEventListener('resize', checkContainerBounds);

    return () => {
      window.removeEventListener('scroll', checkContainerBounds);
      window.removeEventListener('resize', checkContainerBounds);
    };
  }, [headerHeight]);

  const menuItems = Array.isArray(items) ? items : [];
  
  // Get active item ID for optimized useEffect dependency
  const activeItemId = menuItems.find(item => item.isActive)?.id;

  // Auto-scroll active item into view (for programmatic/scroll-spy changes only)
  useEffect(() => {
    // Skip if this was triggered by a click (already scrolled synchronously)
    if (isClickScrollRef.current) {
      isClickScrollRef.current = false;
      return;
    }
    
    if (activeItemRef.current && listRef.current) {
      smoothScrollToCenter(listRef.current, activeItemRef.current);
    }
  }, [activeItemId]);

  // Handle item click - scroll immediately before any state updates
  const handleItemClick = (event, item) => {
    // Mark that we're handling scroll via click
    isClickScrollRef.current = true;
    
    // Scroll the clicked button into view immediately with fast animation
    smoothScrollToCenter(listRef.current, event.currentTarget);
    
    // Then call the provided onClick handler
    if (item.onClick) {
      item.onClick();
    }
  };

  const containerClassName = [
    'mobile-nav-menu',
    isAtBottom && 'mobile-nav-menu--bottom',
    className
  ].filter(Boolean).join(' ').trim();

  return (
    <nav className={containerClassName} style={isAtBottom ? {} : { top: `${headerHeight}px` }}>
      <div className="mobile-nav-menu__list" ref={listRef}>
        {menuItems.map((item) => {
          const isActive = item.isActive;
          const itemClassName = isActive
            ? 'mobile-nav-menu__item mobile-nav-menu__item--active'
            : 'mobile-nav-menu__item';

          return (
            <button
              key={item.id}
              ref={isActive ? activeItemRef : null}
              className={itemClassName}
              onClick={(e) => handleItemClick(e, item)}
              type="button"
            >
              <span className="mobile-nav-menu__label">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

